import React, { useMemo, useState } from "react";

/**
 * AppFooter – professional footer with:
 * 1) Application timelines (production year → current year)
 * 2) Copyright (© …)
 * 3) Compliance links (Terms of Use, Privacy Notice)
 *
 * Env support (CRA-style):
 * - REACT_APP_PRODUCTION_YEAR: number (e.g., "2024")
 * - REACT_APP_ORG_NAME: string (e.g., "Ministry of X" / "COSTECH")
 * - REACT_APP_COPYRIGHT_URL: URL for a copyright/legal page (optional)
 * - REACT_APP_TERMS_URL: URL for “Terms of Use” (optional)
 * - REACT_APP_PRIVACY_URL: URL for “Privacy Notice” (optional)
 *
 * If *_URL envs are missing, the footer can show the content in a modal.
 */

type Props = {
  className?: string;
  minimal?: boolean;

  /** Force modal for links even when *_URL envs exist */
  forceModal?: boolean;

  /** Optionally override values without envs */
  productionYear?: number;
  orgName?: string;
  copyrightUrl?: string;
  termsUrl?: string;
  privacyUrl?: string;
};

const AppFooter: React.FC<Props> = ({
  className = "",
  minimal = false,
  forceModal = false,
  productionYear,
  orgName,
  copyrightUrl,
  termsUrl,
  privacyUrl,
}) => {
  // Read envs (CRA)
  const envProductionYear = Number(process.env.REACT_APP_PRODUCTION_YEAR || "");
  const envOrgName = process.env.REACT_APP_ORG_NAME || "";
  const envCopyrightUrl = process.env.REACT_APP_COPYRIGHT_URL || "";
  const envTermsUrl = process.env.REACT_APP_TERMS_URL || "";
  const envPrivacyUrl = process.env.REACT_APP_PRIVACY_URL || "";

  // Final values (props override env, env override sensible defaults)
  const startYear =
    productionYear || envProductionYear || new Date().getFullYear();
  const organization = (orgName || envOrgName || "Your Organization").trim();

  const currentYear = new Date().getFullYear();
  const timeline = useMemo(() => {
    // If production year is same as current year then show only current year
    if (!startYear || startYear >= currentYear) return `${currentYear}`;
    return `${startYear} - ${currentYear}`;
  }, [startYear, currentYear]);

  const finalCopyrightUrl = (copyrightUrl || envCopyrightUrl || "").trim();
  const finalTermsUrl = (termsUrl || envTermsUrl || "").trim();
  const finalPrivacyUrl = (privacyUrl || envPrivacyUrl || "").trim();

  // Simple modal for Terms/Privacy (only used when no URL is provided OR forceModal is true)
  const [modalOpen, setModalOpen] = useState<
    null | "terms" | "privacy" | "copyright"
  >(null);

  const openLinkOrModal = (kind: "terms" | "privacy" | "copyright") => {
    const url =
      kind === "terms"
        ? finalTermsUrl
        : kind === "privacy"
        ? finalPrivacyUrl
        : finalCopyrightUrl;

    if (!forceModal && url) {
      // Open in new tab by default (professional UX for legal pages)
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setModalOpen(kind);
  };

  const closeModal = () => setModalOpen(null);

  // Minimal sample content for modal (replace with your copy later or route pages)
  const modalTitle =
    modalOpen === "terms"
      ? "Terms of Use"
      : modalOpen === "privacy"
      ? "Privacy Notice"
      : modalOpen === "copyright"
      ? "Copyright"
      : "";

  const modalBody = (
    <div className="space-y-3 text-sm leading-6">
      {modalOpen === "terms" && (
        <p>
          These Terms of Use govern your use of this application. By accessing
          or using the service, you agree to be bound by these terms. (Replace
          this text with the official Terms of Use.)
        </p>
      )}
      {modalOpen === "privacy" && (
        <p>
          This Privacy Notice explains how we collect, use, and protect personal
          data processed by the application. (Replace this text with the
          official Privacy Notice.)
        </p>
      )}
      {modalOpen === "copyright" && (
        <p>
          © {currentYear} {organization}. All rights reserved. (Replace or link
          to your full copyright/notice page.)
        </p>
      )}
    </div>
  );

  if (minimal) {
    return (
      <footer
        className={
          "w-full border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 " +
          "text-xs text-gray-600 dark:text-gray-400 px-4 py-3 flex items-center justify-center gap-3 " +
          className
        }
      >
        <button
          type="button"
          onClick={() => openLinkOrModal("copyright")}
          className="underline underline-offset-2"
        >
          © {currentYear} {organization}
        </button>
        <span>•</span>
        <button
          type="button"
          onClick={() => openLinkOrModal("terms")}
          className="underline underline-offset-2"
        >
          Terms
        </button>
        <span>•</span>
        <button
          type="button"
          onClick={() => openLinkOrModal("privacy")}
          className="underline underline-offset-2"
        >
          Privacy
        </button>
      </footer>
    );
  }

  return (
    <>
      <footer
        className={
          "w-full border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 " +
          "text-gray-600 dark:text-gray-400 text-sm " +
          "px-4 md:px-6 lg:px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-2 " +
          className
        }
      >
        {/* Left: timeline */}
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700 dark:text-gray-200">
            Timeline:
          </span>
          <span aria-label="Application timelines">{timeline}</span>
        </div>

        {/* Center: copyright (clickable) */}
        <button
          type="button"
          className="underline underline-offset-2 hover:text-gray-900 dark:hover:text-gray-200"
          onClick={() => openLinkOrModal("copyright")}
          title="Copyright"
        >
          {/* Professional format: © YEAR ORG */}© {currentYear} {organization}
        </button>

        {/* Right: compliance links */}
        <nav className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => openLinkOrModal("terms")}
            className="underline underline-offset-2 hover:text-gray-900 dark:hover:text-gray-200"
            title="Terms of Use"
          >
            Terms of Use
          </button>
          <button
            type="button"
            onClick={() => openLinkOrModal("privacy")}
            className="underline underline-offset-2 hover:text-gray-900 dark:hover:text-gray-200"
            title="Privacy Notice"
          >
            Privacy Notice
          </button>
        </nav>
      </footer>

      {/* Lightweight modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-footer-modal-title"
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900 shadow-xl p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2
                id="app-footer-modal-title"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                {modalTitle}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full px-2 py-1 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>
            {modalBody}
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppFooter;
